import type { Celok, Zostava } from "@/api/schemas";

/**
 * Celky zostáv v tabuľke (KOLO 235) — skupiny v poradí obrazovky.
 *
 * Zostavy bez celku sú navrchu bez nadpisu, potom každý celok s nadpisom
 * a jeho zostavami — aj prázdny (práve založený), aby sa naň dalo
 * potiahnuť. Server drží zostavy celku pokope (`zarovnaj_kody`), tu sa len
 * delia; zostava s neznámym celkom (stará cache) ide medzi bez celku.
 */
export interface SkupinaZostav {
  celok: Celok | null;
  zostavy: Zostava[];
}

export function skupinyZostav(
  zostavy: readonly Zostava[],
  celky: readonly Celok[],
): SkupinaZostav[] {
  const znamy = new Set(celky.map((c) => c.id));
  const bezCelku: SkupinaZostav = { celok: null, zostavy: [] };
  const poCelkoch = new Map<string, SkupinaZostav>(
    celky.map((c) => [c.id, { celok: c, zostavy: [] }]),
  );
  for (const z of zostavy) {
    const cid = z.celok_id ?? null;
    if (cid && znamy.has(cid)) poCelkoch.get(cid)?.zostavy.push(z);
    else bezCelku.zostavy.push(z);
  }
  const vysledok = [...poCelkoch.values()];
  return bezCelku.zostavy.length > 0 ? [bezCelku, ...vysledok] : vysledok;
}

/** Súčet cien zostáv celku — bez DPH, pred zľavou zákazky (ako ponuka). */
export function cenaCelku(zostavy: readonly Zostava[]): number {
  return zostavy.reduce((s, z) => s + Number(z.cena_celkom ?? 0), 0);
}

/**
 * Posun zostavy hore / dole len v jej skupine (KOLO 235) — do iného celku
 * sa ide ťahaním alebo Dať do celku, inak by ju server vrátil späť.
 */
export function susedVSkupine(
  zostavy: readonly Zostava[],
  id: string,
  smer: -1 | 1,
): boolean {
  const i = zostavy.findIndex((z) => z.id === id);
  const ja = zostavy[i];
  const sused = zostavy[i + smer];
  if (!ja || !sused) return false;
  return (sused.celok_id ?? null) === (ja.celok_id ?? null);
}
